// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react';
import { Link } from 'react-router-dom';

import styles from './Button.module.css';
import { cx } from './cx';

/**
 * `filled` — a quiet tinted button (the Bygning screens' default).
 * `primary` — the one action a surface leads with.
 * `outline` — the legacy screens' bordered button.
 */
export type ButtonVariant = 'filled' | 'primary' | 'outline';
/** `danger` gives the label the crit ink; `primary` ignores it. */
export type ButtonTone = 'neutral' | 'danger';
/**
 * `sm` — dense (28px): forms, dialogs, the inspector's small actions.
 * `md` — bar (32px): toolbars, evidence foot rows, the BulkBar.
 * `lg` — field (32px, body text): the PartBar, review actions.
 * Mirrors `SelectSize`, phone growth included.
 */
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonVisualProps {
  /** Default `filled`. */
  variant?: ButtonVariant;
  /** Default `neutral`. */
  tone?: ButtonTone;
  /** Default `md`. */
  size?: ButtonSize;
  /** Fill the container's width. */
  block?: boolean;
  /** Armed two-step destructive (useArmedConfirm): sets `data-armed`, a crit-tinted fill. */
  armed?: boolean;
  /** A leading icon (an svg, sized to `--space-4`). */
  icon?: ReactNode;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonVisualProps {
  ref?: Ref<HTMLButtonElement>;
}

type AnchorRest = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

/** A ButtonLink goes either to an in-app route (`to`, a router Link) or to a URL (`href`, a plain <a>). */
export type ButtonLinkProps = ButtonVisualProps &
  AnchorRest & { ref?: Ref<HTMLAnchorElement> } & ({ to: string; href?: never } | { href: string; to?: never });

function buttonClass(
  { variant = 'filled', tone = 'neutral', size = 'md', block }: ButtonVisualProps,
  className: string | undefined,
): string {
  return cx(
    styles.button,
    styles[variant],
    styles[size],
    tone === 'danger' && variant !== 'primary' && styles.danger,
    block && styles.block,
    className,
  );
}

function content(icon: ReactNode, children: ReactNode) {
  return (
    <>
      {icon != null && icon !== false && (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </>
  );
}

/**
 * The shared button. `type` defaults to `"button"`; `className` lands on the
 * `<button>` itself (there is no wrapper). Every other prop goes to the
 * `<button>`, so `aria-pressed`, `onBlur={confirm.disarm}` and the event's
 * `currentTarget` reach it unchanged.
 */
export function Button({
  variant,
  tone,
  size,
  block,
  armed,
  icon,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClass({ variant, tone, size, block }, className)}
      data-armed={armed || undefined}
      {...rest}
    >
      {content(icon, children)}
    </button>
  );
}

function isTrue(v: AnchorRest['aria-disabled']): boolean {
  return v === true || v === 'true';
}

/**
 * A link that looks like `Button` (Eksportér CSV, a download). `to` renders a
 * router `Link`, `href` a plain `<a>` (no router needed). `aria-disabled`
 * gives the disabled look and swallows clicks.
 */
export function ButtonLink(props: ButtonLinkProps) {
  const { variant, tone, size, block, armed, icon, className, children, onClick, to, href, ...rest } = props;
  const disabled = isTrue(rest['aria-disabled']);
  const common = {
    className: buttonClass({ variant, tone, size, block }, className),
    'data-armed': armed || undefined,
    onClick: (e: MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      onClick?.(e);
    },
  };
  if (to !== undefined) {
    return (
      <Link to={to} {...rest} {...common}>
        {content(icon, children)}
      </Link>
    );
  }
  return (
    <a href={href} {...rest} {...common}>
      {content(icon, children)}
    </a>
  );
}
